import { Reducer } from '@reduxjs/toolkit';
import {
  UserType,
  PermissionGroup,
  UserPermissionRole,
  ListUser,
  UserName,
  MfaSetupData,
  MfaStatus,
  MultiAccountUser,
  AdminAccount,
  AccountBrand,
  AccountRole,
} from '../../../types/user';
import { AccessibleAccountsResponse } from '../../../types/account';
import { getRoles } from '../../utils/UserUtils';

export type MfaState = {
  mfaRequired: boolean;
  mfaSession: string | null;
  mfaEmail: string | null;
  mfaChallengeName: string | null;
  mfaStatus: MfaStatus | null;
  mfaSetupData: MfaSetupData | null;
  mfaSetupPending: boolean;
  mfaVerifyPending: boolean;
  mfaStatusPending: boolean;
  mfaDisablePending: boolean;
  mfaError: string | null;
};

export type UserState = {
  fetchingUser: boolean;
  error?: any;
  user?: UserType;
  updateSettingsPending: boolean;
  userList: ListUser[];
  userNameList: UserName[];
  permissionGroups: PermissionGroup[];
  roles: UserPermissionRole[];
  accessibleAccounts?: AccessibleAccountsResponse;
  loadingAccessibleAccounts: boolean;
  switchingAccount: boolean;
  // Multi-account user management
  multiAccountUsers: MultiAccountUser[];
  adminAccounts: AdminAccount[];
  fetchingAdminUsersView: boolean;
  accountBrands: { [accountId: number]: AccountBrand[] };
  accountRoles: { [accountId: number]: AccountRole[] };
} & MfaState;

const initialMfaState: MfaState = {
  mfaRequired: false,
  mfaSession: null,
  mfaEmail: null,
  mfaChallengeName: null,
  mfaStatus: null,
  mfaSetupData: null,
  mfaSetupPending: false,
  mfaVerifyPending: false,
  mfaStatusPending: false,
  mfaDisablePending: false,
  mfaError: null,
};

const initialState: UserState = {
  fetchingUser: false,
  error: null,
  user: undefined,
  updateSettingsPending: false,
  userList: [],
  userNameList: [],
  permissionGroups: [],
  roles: [],
  accessibleAccounts: undefined,
  loadingAccessibleAccounts: false,
  switchingAccount: false,
  multiAccountUsers: [],
  adminAccounts: [],
  fetchingAdminUsersView: false,
  accountBrands: {},
  accountRoles: {},
  ...initialMfaState,
};

const reducer: Reducer<UserState> = (state = initialState, action: any) => {
  switch (action.type) {
    case 'FETCH_USERS_FULFILLED': {
      const userList = action.meta.userIds
        ? [...state.userList, ...action.payload.data]
        : action.payload.data;
      return { ...state, userList };
    }
    case 'FETCH_USER_NAMES_FULFILLED': {
      return { ...state, userNameList: action.payload.data };
    }
    case 'FETCH_PERMISSIONS_FULFILLED': {
      const permissionGroups = action.payload.data.groups;
      return { ...state, permissionGroups };
    }
    case 'FETCH_ROLES_FULFILLED': {
      const roles = action.payload.data.roles;
      return { ...state, roles };
    }
    case 'LOAD_USER_BY_TOKEN_PENDING': {
      return { ...state, fetchingUser: true };
    }
    case 'LOAD_USER_BY_TOKEN_REJECTED': {
      return { ...state, fetchingUser: false };
    }
    case 'LOGIN_PENDING': {
      return { ...state, error: null };
    }
    case 'LOGIN_REJECTED': {
      return { ...state, error: action.payload.response };
    }
    case 'LOAD_USER_BY_TOKEN_FULFILLED':
    case 'LOGIN_FULFILLED':
    case 'REFETCH_USER_FULFILLED': {
      const user = {
        ...action.payload.data,
        roles: getRoles(action.payload.data),
      };
      return {
        ...state,
        fetchingUser: false,
        error: null,
        user,
      };
    }
    case 'LOGOUT_REJECTED': {
      return { ...state, error: action.payload.response };
    }
    case 'LOGOUT_PENDING': {
      return { ...state, error: null, user: undefined, accessibleAccounts: undefined };
    }
    case 'CREATE_USER_FULFILLED': {
      const user = {
        ...action.payload.data,
        user_role_ids: action.payload.data.user_role_ids || [],
        brand_permissions: action.payload.data.brand_permissions || [],
      };
      return { ...state, userList: [...state.userList, user] };
    }
    case 'REMOVE_USER': {
      return { ...state, user: undefined, accessibleAccounts: undefined };
    }
    case 'UPDATE_ACCOUNT_INFORMATIONS_PENDING': {
      return { ...state, updateSettingsPending: true };
    }
    case 'UPDATE_ACCOUNT_INFORMATIONS_REJECTED': {
      return { ...state, updateSettingsPending: false };
    }
    case 'UPDATE_ACCOUNT_INFORMATIONS_FULFILLED': {
      const user = {
        ...action.payload.data,
        permissions: state.user ? state.user.permissions : null,
        roles: state.user ? state.user.roles : [],
      };
      return { ...state, user, updateSettingsPending: false };
    }
    case 'UPDATE_USER_STATE_FULFILLED': {
      const updatedUser = action.payload.data;
      const userList = state.userList.map(user =>
        // TODO: We can't just use the user object from the backend, as it is missing fields (e.g. brand_permissions).
        user.id === updatedUser.id ? { ...user, active: updatedUser.active } : user
      );
      return { ...state, userList };
    }
    case 'UPDATE_USER_FULFILLED': {
      const updatedUser = action.payload.data;
      const userList = state.userList.map(user =>
        user.id === updatedUser.id ? updatedUser : user
      );
      return { ...state, userList };
    }
    case 'UPDATE_MAPPED_USER_PERMISSIONS_FULFILLED': {
      const userList = action.payload.data;
      return { ...state, userList };
    }
    case 'UPDATE_USER_ROLE_FULFILLED': {
      const roles = action.payload.data.roles;
      return { ...state, roles };
    }
    case 'DELETE_USER_ROLE_FULFILLED': {
      const roles = state.roles.filter(role => role.id !== action.meta.roleId);
      return { ...state, roles };
    }
    case 'FETCH_ACCESSIBLE_ACCOUNTS_PENDING': {
      return { ...state, loadingAccessibleAccounts: true };
    }
    case 'FETCH_ACCESSIBLE_ACCOUNTS_FULFILLED': {
      return {
        ...state,
        accessibleAccounts: action.payload.data,
        loadingAccessibleAccounts: false,
      };
    }
    case 'FETCH_ACCESSIBLE_ACCOUNTS_REJECTED': {
      return { ...state, loadingAccessibleAccounts: false };
    }
    case 'SWITCH_ACCOUNT_PENDING': {
      return { ...state, switchingAccount: true };
    }
    case 'SWITCH_ACCOUNT_FULFILLED': {
      return { ...state, switchingAccount: false };
    }
    case 'SWITCH_ACCOUNT_REJECTED': {
      return { ...state, switchingAccount: false };
    }
    case 'SWITCH_BACK_ACCOUNT_PENDING': {
      return { ...state, switchingAccount: true };
    }
    case 'SWITCH_BACK_ACCOUNT_FULFILLED': {
      return { ...state, switchingAccount: false };
    }
    case 'SWITCH_BACK_ACCOUNT_REJECTED': {
      return { ...state, switchingAccount: false };
    }
    case 'LOGIN_MFA_REQUIRED': {
      return {
        ...state,
        mfaRequired: true,
        mfaSession: action.payload.session,
        mfaEmail: action.payload.email,
        mfaChallengeName: action.payload.challengeName,
        mfaError: null,
      };
    }
    case 'MFA_VERIFY_PENDING': {
      return {
        ...state,
        mfaVerifyPending: true,
        fetchingUser: true,
        mfaError: null,
      };
    }
    case 'MFA_VERIFY_FULFILLED': {
      const user = {
        ...action.payload.data,
        roles: getRoles(action.payload.data),
      };
      return {
        ...state,
        ...initialMfaState,
        fetchingUser: false,
        error: null,
        user,
      };
    }
    case 'MFA_VERIFY_REJECTED': {
      return {
        ...state,
        mfaError: action.payload?.data?.error || 'Invalid MFA code',
        mfaVerifyPending: false,
        fetchingUser: false,
      };
    }
    case 'CLEAR_MFA_STATE': {
      return {
        ...state,
        ...initialMfaState,
      };
    }
    case 'GET_MFA_STATUS_PENDING': {
      return { ...state, mfaStatusPending: true, mfaError: null };
    }
    case 'GET_MFA_STATUS_FULFILLED': {
      return {
        ...state,
        mfaStatus: action.payload.data.data,
        mfaStatusPending: false,
      };
    }
    case 'GET_MFA_STATUS_REJECTED': {
      return {
        ...state,
        mfaStatusPending: false,
        mfaError: action.payload?.data?.error || 'Failed to get MFA status',
      };
    }
    case 'SETUP_MFA_PENDING': {
      return { ...state, mfaSetupPending: true, mfaError: null, mfaSetupData: null };
    }
    case 'SETUP_MFA_FULFILLED': {
      return {
        ...state,
        mfaSetupData: action.payload.data.data,
        mfaSetupPending: false,
      };
    }
    case 'SETUP_MFA_REJECTED': {
      return {
        ...state,
        mfaSetupPending: false,
        mfaError: action.payload?.data?.error || 'Failed to setup MFA',
      };
    }
    case 'VERIFY_MFA_SETUP_PENDING': {
      return { ...state, mfaVerifyPending: true, mfaError: null };
    }
    case 'VERIFY_MFA_SETUP_FULFILLED': {
      return {
        ...state,
        mfaVerifyPending: false,
        mfaSetupData: null,
        mfaStatus: {
          mfa_enabled: true,
          mfa_type: 'SOFTWARE_TOKEN',
          preferred_mfa: 'SOFTWARE_TOKEN_MFA',
        },
      };
    }
    case 'VERIFY_MFA_SETUP_REJECTED': {
      return {
        ...state,
        mfaVerifyPending: false,
        mfaError: action.payload?.data?.error || 'Invalid verification code',
      };
    }
    case 'DISABLE_MFA_PENDING': {
      return { ...state, mfaDisablePending: true, mfaError: null };
    }
    case 'DISABLE_MFA_FULFILLED': {
      return {
        ...state,
        mfaDisablePending: false,
        mfaStatus: { mfa_enabled: false },
      };
    }
    case 'DISABLE_MFA_REJECTED': {
      return {
        ...state,
        mfaDisablePending: false,
        mfaError: action.payload?.data?.error || 'Failed to disable MFA',
      };
    }
    case 'SSO_LOGIN_PENDING': {
      return { ...state, fetchingUser: true, error: null };
    }
    case 'SSO_LOGIN_FULFILLED': {
      const user = {
        ...action.payload.data,
        roles: getRoles(action.payload.data),
      };
      return {
        ...state,
        fetchingUser: false,
        error: null,
        user,
      };
    }
    case 'SSO_LOGIN_REJECTED': {
      return {
        ...state,
        fetchingUser: false,
        error: action.payload?.data?.error || 'SSO login failed',
      };
    }
    case 'FETCH_ADMIN_USERS_VIEW_PENDING': {
      return { ...state, fetchingAdminUsersView: true };
    }
    case 'FETCH_ADMIN_USERS_VIEW_FULFILLED': {
      return {
        ...state,
        multiAccountUsers: action.payload.data.users,
        adminAccounts: action.payload.data.admin_accounts,
        fetchingAdminUsersView: false,
      };
    }
    case 'FETCH_ADMIN_USERS_VIEW_REJECTED': {
      return { ...state, fetchingAdminUsersView: false };
    }
    case 'UPDATE_USER_ACCOUNT_ASSIGNMENTS_FULFILLED': {
      const updatedUser = action.payload.data.user;
      const multiAccountUsers = state.multiAccountUsers.map(user =>
        user.id === updatedUser.id ? updatedUser : user
      );
      return { ...state, multiAccountUsers };
    }
    case 'FETCH_ACCOUNT_BRANDS_FULFILLED': {
      const accountId = action.meta.accountId;
      return {
        ...state,
        accountBrands: {
          ...state.accountBrands,
          [accountId]: action.payload.data.brands,
        },
      };
    }
    case 'FETCH_ACCOUNT_ROLES_FULFILLED': {
      const accountId = action.meta.accountId;
      return {
        ...state,
        accountRoles: {
          ...state.accountRoles,
          [accountId]: action.payload.data.roles,
        },
      };
    }
  }
  return state;
};

export default reducer;
